<template>
    <Dialog
        :value="true"
        :header-text="$t('Take a vacation or mark an absence')"
        :width="500"
        persistent
        fixed
        @close_modal="$emit('close')"
    >
        <v-row>
            <v-col
                cols="12"
                class="pt-0"
            >
                <v-radio-group
                    :value="mainCalendarType"
                    class="mt-0 pt-0"
                    row
                    :error-messages="errors['type']"
                    :disabled="!canEdit"
                    hide-details
                    @change="setMainCalendarType($event); errors['type'] = null"
                >
                    <v-radio
                        color="primary"
                        value="vacation_types"
                    >
                        <template v-slot:label>
                            <div class="body-2">
                                {{ $t('Vacation') }}
                            </div>
                        </template>
                    </v-radio>
                    <v-radio
                        class="ml-4"
                        color="primary"
                        value="away_types"
                    >
                        <template v-slot:label>
                            <div class="body-2">
                                {{ $t('Away') }}
                            </div>
                        </template>
                    </v-radio>
                    <v-radio
                        class="ml-4"
                        color="primary"
                        value="flextime_types"
                    >
                        <template v-slot:label>
                            <div class="body-2">
                                {{ $t('Flextime bonus') }}
                            </div>
                        </template>
                    </v-radio>
                </v-radio-group>
            </v-col>
            <v-col cols="6">
                <div class="caption g5--text mt-1">{{ $t('Company') }}</div>
                <div class="body-1">{{ companyName }}</div>
            </v-col>
            <v-col
                cols="6"
                class="pb-0"
            >
                <dropdown
                    :items="assignableUsers"
                    :selected="assignableUsers.filter(x => x.id === approverId)"
                    name-key="name"
                    value-key="id"
                    :error-messages="errors['approver_id']"
                    show-selected-first
                    :disabled="!canEdit"
                    :label="$t('Approver') + ' *'"
                    @input="approverId = $event.id; errors['approver_id'] = null"
                />
            </v-col>
            <v-col cols="6">
                <div class="caption g5--text">{{ $t('Member') }}</div>
                <div class="body-1">{{ personName }}</div>
            </v-col>
            <v-col cols="6">
                <template v-if="mainCalendarType === 'vacation_types'">
                    <div class="caption g5--text">{{ $t('Vacation available') }}</div>
                    <div class="title">
                        {{ computedVacationCredit }} {{ $t('days') }}
                    </div>
                </template>
                <template v-else-if="mainCalendarType === 'flextime_types'">
                    <div class="caption g5--text">{{ $t('Flextime bonus') }}</div>
                    <div class="body-1" :class="flextimeCredit < 0 ? 'error--text' : 'tretiary--text text--darken-1'">
                        {{ formatFlextimeDuration(flextimeCredit) }}
                    </div>
                </template>
            </v-col>
            <v-col
                v-if="isAllDayOptionVisible"
                cols="12"
                class="pt-0"
            >
                <checkbox
                    :value="isAllDaySelected"
                    :label="$t('All day(s)')"
                    :disabled="!canEdit"
                    @change="onChangeIsAllDay"
                />
            </v-col>
            <v-col cols="4" class="pr-1">
                <date-picker
                    :value="dateFrom"
                    :disabled="!canEdit"
                    :error-messages="errors['date_from']"
                    :max="isAllDay ? dateUntil : null"
                    :label="$t('Start date') + ' *'"
                    @input="onSetStartDate"
                />
            </v-col>
            <template
                v-if="isAllDay"
            >
                <v-col
                    cols="4"
                    class="px-1"
                >
                    <date-picker
                        :value="dateUntil"
                        :disabled="!canEdit"
                        :min="dateFrom"
                        :error-messages="errors['date_until']"
                        :label="$t('End date') + ' *'"
                        @input="onSetEndDate"
                    />
                </v-col>
                <v-col cols="1" />
            </template>
            <v-col
                v-else
                cols="5"
                class="d-flex px-1 align-center"
            >
                <div class="pr-1">
                    <text-field
                        :value="timeFromFormatted"
                        :label="$t('From')"
                        :error-messages="errors['time_from']"
                        :disabled="!canEdit"
                        @input="timeFromFormatted = $event; errors['time_from'] = null"
                        @blur="setFormattedTimeFrom"
                    />
                </div>
                <div class="pl-1">
                    <text-field
                        :value="timeUntilFormatted"
                        :label="$t('Until')"
                        :error-messages="errors['time_until']"
                        :disabled="!canEdit"
                        @input="timeUntilFormatted = $event; errors['time_until'] = null"
                        @blur="setFormattedTimeUntil"
                    />
                </div>
            </v-col>
            <v-col cols="3">
                <div class="caption g5--text d-flex">
                    <span class="mr-1">{{ isAllDayOptionVisible ? $t('Hours') : $t('Days') }}</span>
                    <v-menu v-if="vacationDuration && vacationDuration.days">
                        <template v-slot:activator="{ on }">
                            <div v-on="on" class="cursor-pointer">
                                <Icon icon="HelpCircle" size="16"/>
                            </div>
                        </template>
                        <VacationDurationList :vacation-duration="vacationDuration"/>
                    </v-menu>
                </div>
                <div class="title mt-3">
                    {{ isAllDayOptionVisible ? totalHours : totalDays }}
                </div>
            </v-col>
            <v-col
                v-if="options.length > 1"
                cols="12"
                class="mb-4"
            >
                <dropdown
                    :items="options"
                    :selected="options.filter(x => x.value === selectedVacationType)"
                    name-key="text"
                    value-key="value"
                    show-selected-first
                    :disabled="!canEdit"
                    :label="$t('Type')"
                    @input="selectedVacationType = $event.value; errors['type'] = null"
                />
                <div class="mt-1">
                    <switch-button
                        :value="reduceWorkingHours"
                        :label="$t('Reduce agreed working hours')"
                        :disabled="!canEdit"
                        @change="reduceWorkingHours = $event ? 1 : 0"
                    />
                </div>
            </v-col>
            <v-col cols="12">
                <text-area-field
                    :value="comment"
                    :label="$t('Notes')"
                    :max-length="255"
                    :error-messages="errors['comment']"
                    :disabled="!canEdit"
                    @input="comment = $event; errors['comment'] = null"
                />
            </v-col>
        </v-row>
        <template slot="footer">
            <template v-if="allowedToDeleteVacation">
                <icon-button
                    icon="Trash"
                    @click="onDelete"
                >
                    {{ $t('Delete') }}
                </icon-button>
            </template>
            <template v-if="canEditCompany && calendar && calendar.id">
                <danger-button
                    v-if="calendar.status === CALENDAR_STATUS.IN_REVIEW || calendar.status === CALENDAR_STATUS.APPROVED"
                    @click="onSave(CalendarEventModule.CALENDAR_STATUS.REJECTED)"
                >
                    {{ $t('Reject') }}
                </danger-button>
                <secondary-button
                    v-if="calendar.status === CALENDAR_STATUS.IN_REVIEW || calendar.status === CALENDAR_STATUS.REJECTED"
                    color="tretiary"
                    @click="onSave(CalendarEventModule.CALENDAR_STATUS.APPROVED)"
                >
                    {{ $t('Approve') }}
                </secondary-button>
            </template>
            <v-spacer/>
            <secondary-button
                @click="$emit('close')">
                {{ $t('Cancel') }}
            </secondary-button>
            <primary-button
                :disabled="!canEdit"
                :loading="loader.isLoading"
                @click="onSave(CalendarEventModule.CALENDAR_STATUS.IN_REVIEW)"
            >
                {{ $t('Save') }}
            </primary-button>
        </template>
    </Dialog>
</template>

<script>
import Vue from "vue";
import {$t} from "@/i18n";
import {computed, ref, watch, reactive, onMounted} from "@vue/composition-api";
import Dialog from "@/components/Dialog";
import ApiCalendar from "@/api/calendar/calendar";
import {
    formatTimeDuration,
    parseTimeDuration,
    formatFlextimeDuration,
    parseTime,
    formatDate,
    formatTime, parseSysTime, formatSysDate, parseSysDateTime, formatSysDateTime, formatSysTime, formatDaysFromDuration
} from "@/plugins/formatting";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import Loader from "@/utility/loader";
import AppModule from "@/store/modules/app";
import {deepClone} from "@/helpers/helpers";
import CalendarEventModule, {CALENDAR_STATUS} from "@/modules/calendar/calendar"
import RightsModule from "@/composables/use-rights";
import ValidationModule from "@/modules/validation/validation"
import {calcDateDiff, parseSysDate} from "@/plugins/formatting";
import AccountModule from "@/store/modules/account";
import ApiVacationDuration from '@/api/calendar/vacation_duration';
import VacationDurationList from "@/components/Flextime/VacationDurationList";
import UserModule from "@/store/modules/user";

export default {
    name: "FlextimeVacationModal",
    components: {
        Dialog,
        VacationDurationList
    },
    props: {
        calendar: {
            type: Object,
            default: null
        },
        companyId: {
            type: Number,
            required: true
        },
        companyName: {
            type: String,
            required: true
        },
        personId: {
            type: Number,
            required: true
        },
        personName: {
            type: String,
            required: true,
        },
        flextimeCredit: {
            type: Number,
            required: true
        },
        vacationCredit: {
            type: Number,
            required: true
        }
    },
    setup(props, ctx) {
        const {fetchClassifiers, calendar_types} = AppModule();
        const userModule = UserModule();
        const {can_edit_account} = RightsModule();
        const {getAccount, current_account} = AccountModule();
        const items = ref([]);
        const loader = reactive(new Loader());
        const vacationDurationLoader = reactive(new Loader());
        const selectedVacationType = ref(null);
        const isAllDaySelected = ref(true);
        const hours = ref(null);
        const dateFrom = ref(null);
        const timeFrom = ref('09:00:00');
        const timeFromFormatted = ref('09:00');
        const dateUntil = ref(null);
        const timeUntil = ref('17:00:00');
        const timeUntilFormatted = ref('17:00');
        const comment = ref("");
        const formattedHours = ref("");
        const mainCalendarType = ref("vacation_types");
        const errors = ref({});
        const vacationDuration = ref(null);
        const reduceWorkingHours = ref(1);
        const approverId = ref(null)

        const companyAccount = computed(() => getAccount(props.companyId).value)

        const canEdit = computed(() => {
            if (!props.calendar) {
                return true
            }

            if (!props.calendar.id) {
                return true
            }

            return !CalendarEventModule.LOCKED_CALENDAR_STATUSES.includes(props.calendar.status);
        })
        const canEditCompany = computed(() => can_edit_account(props.companyId))

        const updateFormattedTimeFrom = async () => {
            timeFromFormatted.value = formatTime(timeFrom.value);
        }

        const setFormattedTimeFrom = (event) => {
            let value = event.target.value
            value = value.trim()

            let parsedTime = parseTime(value);

            if (parsedTime) {
                let timeDuration = parseTimeDuration(parsedTime)
                if (timeDuration >= (60 * 60 * 23)) {
                    parsedTime = '22:59:00'
                }

                timeFrom.value = parsedTime
            }

            updateFormattedTimeFrom()

            setMinimumTimeForTimeUntil()

            getVacationDuration()
        }

        const updateFormattedTimeUntil = async () => {
            timeUntilFormatted.value = formatTime(timeUntil.value);
        }

        const setMinimumTimeForTimeUntil = () => {
            if (!timeFrom.value) {
                return
            }

            const sysTimeFrom = parseSysTime(timeFrom.value)
            let sysTimeUntil = parseSysTime(timeUntil.value)

            const duration = sysTimeUntil - sysTimeFrom

            if (duration < (60 * 60 * 1000)) {
                sysTimeUntil = sysTimeFrom
                sysTimeUntil.setHours(sysTimeUntil.getHours() + 1)
            }

            timeUntil.value = formatSysTime(sysTimeUntil)
            updateFormattedTimeUntil()
        }

        const setFormattedTimeUntil = (event) => {
            let value = event.target.value
            value = value.trim()

            const parsedTime = parseTime(value);
            if (parsedTime) {
                timeUntil.value = parsedTime
            }

            updateFormattedTimeUntil()

            setMinimumTimeForTimeUntil()

            getVacationDuration()
        }

        const options = computed(() => {
            let items = []
            for (const calendarType in calendar_types.value) {
                if (mainCalendarType.value === calendarType) {
                    for (const calendarValue of calendar_types.value[calendarType]) {
                        let _text = calendarValue;
                        if (calendarValue === "away") _text = $t("away_type_Away")
                        else if (calendarValue === "sick_leave") _text = $t("Sick leave")
                        else if (calendarValue === "parental_leave") _text = $t("Parental leave")
                        items.push({
                            value: calendarValue,
                            text: _text,
                        })
                    }
                }
            }

            return items
        })

        const onChangeIsAllDay = (value) => {
            hours.value = null;
            formattedHours.value = null;
            isAllDaySelected.value = value

            if (!isAllDay.value) {
                dateUntil.value = dateFrom.value
                errors.value.date_until = null
            }

            getVacationDuration()
        }

        const getSaveDateFrom = () => {
            let dateFromToSave = dateFrom.value + ' '
            if (isAllDay.value) {
                dateFromToSave += '00:00:00'
            } else {
                dateFromToSave += timeFrom.value
            }

            return dateFromToSave
        }

        const getSaveDateUntil = () => {
            let dateUntilToSave = (isAllDay.value ? dateUntil.value : dateFrom.value) + ' '

            if (isAllDay.value) {
                dateUntilToSave += '00:00:00'
            } else {
                dateUntilToSave += timeUntil.value
            }

            let dateUntilToSaveDate = parseSysDateTime(dateUntilToSave)
            let currentDateUntilToSaveDate = parseSysDateTime(dateUntilToSave)
            currentDateUntilToSaveDate.setSeconds(dateUntilToSaveDate.getSeconds() - 1)
            if (currentDateUntilToSaveDate.getDate() !== dateUntilToSaveDate.getDate()) {
                dateUntilToSaveDate.setDate(dateUntilToSaveDate.getDate() + 1)
            }

            dateUntilToSaveDate = formatSysDateTime(dateUntilToSaveDate)

            return dateUntilToSaveDate
        }

        const onSave = async (status) => {
            await loader.withLoader(async () => {
                try {
                    if (status === CalendarEventModule.CALENDAR_STATUS.IN_REVIEW && approverId.value === current_account.value.id) {
                        status = CalendarEventModule.CALENDAR_STATUS.APPROVED
                    }

                    let dateFromToSave = getSaveDateFrom()
                    let dateUntilToSave = getSaveDateUntil()

                    const payload = {
                        comment: comment.value,
                        date_from: dateFromToSave,
                        date_until: dateUntilToSave,
                        duration: isAllDay.value ? 0 : hours.value,
                        company_id: props.companyId,
                        person_id: props.personId,
                        approver_id: approverId.value,
                        type: selectedVacationType.value,
                        status: status,
                        expected_duration: vacationDuration.value && vacationDuration.value.expected_period_duration,
                        reduce_working_hours: canChangeReduceWorkingHours.value ? reduceWorkingHours.value : 0
                    }

                    await ApiCalendar.saveCalendarEvent(props.calendar ? props.calendar.id : null, payload)
                    ctx.emit('update')
                    ctx.emit('close')
                } catch (e) {
                    errors.value = ValidationModule.parseErrors(e)
                    if (!ValidationModule.hasErrors(errors.value)) {
                        errorDialog.showError(e)
                    }
                }
            })
        }

        const onDelete = async () => {
            try {
                await ApiCalendar.deleteCalendarEvent(props.calendar.id)
                ctx.emit('update')
                ctx.emit('close')
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const assignableUsers = computed(() => {
            return userModule.getActiveMembersByCompany(props.companyId)
                .filter(x => x.type === "person")
                .filter(x => ['admin', 'owner'].includes(x.role))
        })

        onMounted(async () => {
            try {
                await userModule.loadMembers()

                await fetchClassifiers({types: ["calendar_types"]})
                setMainCalendarType("vacation_types")

                if (props.calendar) {
                    let dateUntilToSaveDate = parseSysDateTime(props.calendar.date_until)
                    dateUntilToSaveDate.setSeconds(dateUntilToSaveDate.getSeconds() - 1)
                    dateUntilToSaveDate = formatSysDate(dateUntilToSaveDate)

                    selectedVacationType.value = props.calendar.type
                    isAllDaySelected.value = props.calendar.is_all_day
                    hours.value = props.calendar.duration ? props.calendar.duration : null
                    dateFrom.value = props.calendar.date_from.split(" ")[0]
                    timeFrom.value = props.calendar.date_from.split(" ")[1]
                    dateUntil.value = dateUntilToSaveDate
                    timeUntil.value = props.calendar.date_until.split(" ")[1]
                    comment.value = props.calendar.comment
                    approverId.value = props.calendar.approver_id
                    selectedVacationType.value = props.calendar.type
                    reduceWorkingHours.value = props.calendar.reduce_working_hours

                    for (const calendarKey in calendar_types.value) {
                        for (const value of calendar_types.value[calendarKey]) {
                            if (value === selectedVacationType.value) {
                                mainCalendarType.value = calendarKey
                            }
                        }
                    }

                    updateFormattedTimeFrom()
                    updateFormattedTimeUntil()

                    getVacationDuration()
                }
            } catch (e) {
                errorDialog.showError(e)
            }
        })

        const onReject = async () => {
            const calendarCopy = deepClone(props.calendar)
            calendarCopy.status = CalendarEventModule.CALENDAR_STATUS.REJECTED

            try {
                await ApiCalendar.saveCalendarEvent(calendarCopy.id, calendarCopy)
            } catch (e) {
                errorDialog.showError(e)
            }

            ctx.emit('update')
            ctx.emit('close')
        }

        const setMainCalendarType = (value) => {
            mainCalendarType.value = value
            selectedVacationType.value = options.value.length ? options.value[0].value : null
            getVacationDuration()
        }

        const computedVacationCredit = computed(() => {
            return props.vacationCredit ? props.vacationCredit / (60 * 60 * 24) : 0
        })

        const isAllDayOptionVisible = computed(() => {
            return mainCalendarType.value !== 'vacation_types'
        })

        const isAllDay = computed(() => {
            if (mainCalendarType.value === 'vacation_types') {
                return true
            }

            return isAllDaySelected.value
        })

        const totalHours = computed(() => {
            if (!vacationDuration.value) {
                return "-"
            }

            return formatTimeDuration(vacationDuration.value.expected_period_duration)
        })

        const totalDays = computed(() => {
            if (!vacationDuration.value) {
                return "-"
            }

            return formatDaysFromDuration(vacationDuration.value.expected_period_duration)
        })

        const onSetStartDate = (e) => {
            dateFrom.value = e;
            errors.value['date_from'] = null;

            if (!isAllDay.value) {
                dateUntil.value = e;
                errors.value['date_from'] = null;
            }

            getVacationDuration()
        }

        const onSetEndDate = (e) => {
            dateUntil.value = e;
            errors.value['date_until'] = null;

            getVacationDuration()
        }

        const getVacationDuration = async () => {
            if (!dateFrom.value) {
                return false
            }

            if (!dateUntil.value) {
                return false
            }

            let dateFromToSave = getSaveDateFrom()
            let dateUntilToSave = getSaveDateUntil()

            await vacationDurationLoader.withLoader(async () => {
                try {
                    let data = await ApiVacationDuration.getVacationDuration(
                        props.companyId,
                        props.personId,
                        props.calendar ? props.calendar.timezone : companyAccount.value.timezone,
                        props.calendar ? props.calendar.country_code : companyAccount.value.address_country_code,
                        dateFromToSave,
                        dateUntilToSave,
                        selectedVacationType.value
                    )

                    vacationDuration.value = data
                } catch (e) {
                    errorDialog.showError(e)
                }
            })
        }

        const isCalendarPersonAdmin = computed(() => {
            return assignableUsers.value.some(x => x.id === props.personId)
        })

        const canChangeReduceWorkingHours = computed(() => {
            return ['away', 'sick_leave', 'parental_leave'].includes(selectedVacationType.value)
        })

        const allowedToDeleteVacation = computed(() => {
            const allowedStatuses = [CALENDAR_STATUS.IN_REVIEW, CALENDAR_STATUS.REJECTED]
            return props.calendar && props.calendar.id && (canEditCompany.value || allowedStatuses.includes(props.calendar.status))
        })

        return {
            items,
            loader,
            options,
            selectedVacationType,
            formatTimeDuration,
            isAllDay,
            hours,
            dateFrom,
            timeFrom,
            dateUntil,
            timeUntil,
            comment,
            onSave,
            onChangeIsAllDay,
            formattedHours,
            timeUntilFormatted,
            timeFromFormatted,
            setFormattedTimeFrom,
            setFormattedTimeUntil,
            formatFlextimeDuration,
            onReject,
            mainCalendarType,
            setMainCalendarType,
            canEditCompany,
            canEdit,
            computedVacationCredit,
            ValidationModule,
            errors,
            onDelete,
            CalendarEventModule,
            totalHours,
            isAllDaySelected,
            isAllDayOptionVisible,
            onSetStartDate,
            totalDays,
            onSetEndDate,
            vacationDuration,
            formatDate,
            reduceWorkingHours,
            canChangeReduceWorkingHours,
            CALENDAR_STATUS,
            assignableUsers,
            approverId,
            isCalendarPersonAdmin,
            allowedToDeleteVacation,
        }
    }
}
</script>

<style scoped lang="scss">
::v-deep textarea {
    height: 70px;
}
</style>